import { useState } from 'react';
import ConfirmModal from '../ConfirmModal';
import { formatDate } from '../../lib/format';

/** The banner shown in place of an editable form once a proposal is signed.
 *  "Edit anyway" is the ONLY way in: it opens a modal that requires typing
 *  the client's name before override editing unlocks. */
export function SignedLockBanner({
  signedBy,
  signedAt,
  clientName,
  onEnterOverride,
}: {
  signedBy: string | null;
  signedAt: number | null;
  clientName: string;
  onEnterOverride: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="rounded-2xl border border-gold-soft bg-[#fbf6ec] p-6">
      <p className="text-sm text-ink">
        Signed by <strong className="font-semibold">{signedBy ?? 'the client'}</strong>
        {signedAt && ` on ${formatDate(signedAt)}`}. Content is locked.
      </p>
      <p className="mt-1.5 text-sm text-ink-soft">
        Nothing here can be edited until you deliberately unlock it. This protects what the signed client actually
        agreed to.
      </p>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className="mt-4 rounded-full border border-ink-faint px-4 py-1.5 text-sm font-medium text-ink-soft transition hover:border-ink hover:text-ink"
      >
        Edit anyway
      </button>

      <ConfirmModal
        open={confirmOpen}
        tone="danger"
        title="Edit a signed proposal"
        message={
          <>
            This changes what a client who already signed will see if they revisit the link. Only do this to fix a
            genuine mistake, not to change agreed terms after the fact. Saves while unlocked go through a separate,
            clearly marked path.
          </>
        }
        confirmLabel="Unlock editing"
        requireTypedConfirmation={clientName}
        onConfirm={() => {
          setConfirmOpen(false);
          onEnterOverride();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/** Reset-signature: the only sanctioned way to un-sign a proposal. Its own
 *  double-confirm (the button below, plus the typed client name inside the
 *  modal), sober copy, no euphemisms about what it does. */
export function ResetSignatureControl({
  clientName,
  onReset,
}: {
  clientName: string;
  onReset: (confirm: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(typed?: string) {
    if (!typed) return;
    setBusy(true);
    setError(null);
    try {
      await onReset(typed);
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not reset the signature.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-error/30 bg-error-tint p-6">
      <h3 className="font-display text-lg text-ink">Danger zone</h3>
      <p className="mt-1.5 text-sm text-ink-soft">
        Resetting the signature deletes the recorded signature and returns this proposal to Published. The client
        will need to sign again. This does not undo any work already delivered or invoiced.
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 rounded-full border border-error px-4 py-1.5 text-sm font-medium text-error transition hover:bg-error/10"
      >
        Reset signature
      </button>

      <ConfirmModal
        open={open}
        tone="danger"
        title="Reset this signature"
        message="This deletes the signature record and moves the proposal back to Published, unsigned. Confirm you mean to do this, not just view it."
        confirmLabel="Reset signature"
        requireTypedConfirmation={clientName}
        busy={busy}
        errorMessage={error}
        onConfirm={handleConfirm}
        onCancel={() => (busy ? null : setOpen(false))}
      />
    </div>
  );
}
